// src/pages/Subjects.tsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Box, Typography, Paper, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Chip, Stack,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    FormControl, InputLabel, Select, MenuItem, FormControlLabel,
    Switch, CircularProgress, Snackbar, Alert, InputAdornment,
    Tooltip, Divider,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    LibraryBooks as SubjectIcon,
    CurrencyRupee as RupeeIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";

import {
    getAllSubjectsAdmin, createSubject, updateSubject, deleteSubject,
    getClasses, getMediums, Subject, SubjectUpsertDto,
} from "../services/subjectService";
import { TableShimmer } from "../components/Shimmer";

const PRIMARY = "#174143";
const ACCENT = "#3d9ea4";

const fadeUp = keyframes`
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
`;

const emptyForm: SubjectUpsertDto = {
    name: "", classId: 0, mediumId: 0,
    monthlyPrice: 0, quarterlyPrice: 0, halfYearlyPrice: 0, yearlyPrice: 0,
    purchasable: true,
};

export default function SubjectsPage(): React.ReactElement {
    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [classes, setClasses] = useState<any[]>([]);
    const [mediums, setMediums] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ type: "success" | "error"; msg: string } | null>(null);

    // filters
    const [search, setSearch] = useState("");
    const [filterClass, setFilterClass] = useState<number>(0);
    const [filterMedium, setFilterMedium] = useState<number>(0);
    const [filterStatus, setFilterStatus] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");

    // dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState<SubjectUpsertDto>(emptyForm);

    // ── Data ──────────────────────────────────────────────
    const loadMeta = async () => {
        try {
            const [cls, med] = await Promise.all([getClasses(0, 100), getMediums()]);
            setClasses(Array.isArray(cls) ? cls : []);
            setMediums(Array.isArray(med) ? med : []);
        } catch (e) { console.error("meta failed", e); }
    };

    const fetchSubjects = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const data = await getAllSubjectsAdmin(0, 200);
            setSubjects(Array.isArray(data) ? data : (data?.content ?? []));
        } catch (err: any) {
            setError(err.message || "Failed to load subjects");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { loadMeta(); fetchSubjects(); }, [fetchSubjects]);

    // ── Filtered list ─────────────────────────────────────
    const filtered = useMemo(() => {
        return subjects.filter((s) => {
            const q = search.toLowerCase();
            if (q && !s.name.toLowerCase().includes(q)) return false;
            const cId = s.classId || s.schoolClass?.id;
            const mId = s.mediumId || s.medium?.id;
            if (filterClass && cId !== filterClass) return false;
            if (filterMedium && mId !== filterMedium) return false;
            if (filterStatus === "ACTIVE" && !s.purchasable) return false;
            if (filterStatus === "INACTIVE" && s.purchasable) return false;
            return true;
        });
    }, [subjects, search, filterClass, filterMedium, filterStatus]);

    // ── Helpers ───────────────────────────────────────────
    const getClassName = (id?: number) => classes.find(c => c.id === id)?.name ?? "—";
    const getMediumName = (id?: number) => mediums.find(m => m.id === id)?.name ?? "—";

    // ── Handlers ──────────────────────────────────────────
    const handleOpenCreate = () => {
        setEditingId(null);
        setForm({ ...emptyForm, classId: classes[0]?.id ?? 0, mediumId: mediums[0]?.id ?? 0 });
        setOpenDialog(true);
    };

    const handleOpenEdit = (sub: Subject) => {
        setEditingId(sub.id);
        setForm({
            name: sub.name,
            classId: sub.classId || sub.schoolClass?.id || 0,
            mediumId: sub.mediumId || sub.medium?.id || 0,
            monthlyPrice: sub.monthlyPrice || 0,
            quarterlyPrice: sub.quarterlyPrice || 0,
            halfYearlyPrice: sub.halfYearlyPrice || 0,
            yearlyPrice: sub.yearlyPrice || 0,
            purchasable: sub.purchasable,
        });
        setOpenDialog(true);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Delete this subject? This cannot be undone.")) return;
        try {
            await deleteSubject(id);
            setSnackbar({ type: "success", msg: "Subject deleted" });
            fetchSubjects();
        } catch (err: any) {
            setSnackbar({ type: "error", msg: err.message || "Delete failed" });
        }
    };

    const handleSubmit = async () => {
        if (!form.name.trim() || !form.classId || !form.mediumId) {
            setSnackbar({ type: "error", msg: "Name, Class and Medium are required." });
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await updateSubject(editingId, form);
                setSnackbar({ type: "success", msg: "Subject updated" });
            } else {
                await createSubject(form);
                setSnackbar({ type: "success", msg: "Subject created" });
            }
            setOpenDialog(false);
            fetchSubjects();
        } catch (err: any) {
            setSnackbar({ type: "error", msg: err.message || "Operation failed" });
        } finally { setSaving(false); }
    };

    const activeCount = subjects.filter(s => s.purchasable).length;
    const inactiveCount = subjects.length - activeCount;

    // ── Render ────────────────────────────────────────────
    return (
        <Box sx={{ minHeight: "100vh", background: "#f5f6fa", p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1400, mx: "auto" }}>

                {/* ── Hero Header ── */}
                <Paper
                    elevation={0}
                    sx={{
                        mb: 3, borderRadius: 4, overflow: "hidden",
                        background: `linear-gradient(135deg, ${PRIMARY} 0%, #2a6b6f 60%, ${ACCENT} 100%)`,
                        color: "#fff",
                        animation: `${fadeUp} 0.4s ease-out`,
                        position: "relative",
                    }}
                >
                    <Box sx={{ position: "absolute", top: -50, right: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
                    <Box sx={{ position: "absolute", bottom: -20, right: 120, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.07)", pointerEvents: "none" }} />

                    <Box sx={{ p: { xs: 3, md: 4 }, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, position: "relative" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: 2.5, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <SubjectIcon sx={{ fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.5 }}>Subject Management</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>
                                    Create, edit and manage all subjects, pricing, and availability
                                </Typography>
                            </Box>
                        </Box>

                        {/* Summary Chips */}
                        <Stack direction="row" spacing={1.5} flexWrap="wrap">
                            <Chip label={`${subjects.length} Total`} sx={{ background: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 700 }} />
                            <Chip label={`${activeCount} Active`} sx={{ background: "rgba(67,233,123,0.25)", color: "#fff", fontWeight: 700 }} />
                            <Chip label={`${inactiveCount} Inactive`} sx={{ background: "rgba(255,100,100,0.25)", color: "#fff", fontWeight: 700 }} />
                        </Stack>
                    </Box>
                </Paper>

                {/* ── Toolbar ── */}
                <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 2, mb: 2.5, animation: `${fadeUp} 0.4s ease-out 60ms both` }}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                        {/* Search */}
                        <TextField
                            size="small"
                            placeholder="Search subjects…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
                            sx={{ minWidth: 200, flex: 1 }}
                        />

                        {/* Filter: class */}
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Class</InputLabel>
                            <Select value={filterClass} label="Class" onChange={e => setFilterClass(Number(e.target.value))}>
                                <MenuItem value={0}>All Classes</MenuItem>
                                {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>

                        {/* Filter: medium */}
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Medium</InputLabel>
                            <Select value={filterMedium} label="Medium" onChange={e => setFilterMedium(Number(e.target.value))}>
                                <MenuItem value={0}>All Mediums</MenuItem>
                                {mediums.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                            </Select>
                        </FormControl>

                        {/* Filter: status */}
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Status</InputLabel>
                            <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value as any)}>
                                <MenuItem value="ALL">All</MenuItem>
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="INACTIVE">Inactive</MenuItem>
                            </Select>
                        </FormControl>

                        <Box sx={{ flex: 1 }} />

                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchSubjects} sx={{ border: "1px solid", borderColor: "divider" }}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                            sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, boxShadow: "0 4px 14px rgba(23,65,67,0.3)", fontWeight: 700, px: 2.5, borderRadius: 2 }}
                        >
                            New Subject
                        </Button>
                    </Box>

                    {/* Active filter pills */}
                    {(search || filterClass || filterMedium || filterStatus !== "ALL") && (
                        <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Filtered: {filtered.length} of {subjects.length}</Typography>
                            {search && <Chip size="small" label={`"${search}"`} onDelete={() => setSearch("")} />}
                            {filterClass && <Chip size="small" label={getClassName(filterClass)} onDelete={() => setFilterClass(0)} />}
                            {filterMedium && <Chip size="small" label={getMediumName(filterMedium)} onDelete={() => setFilterMedium(0)} />}
                            {filterStatus !== "ALL" && <Chip size="small" label={filterStatus} onDelete={() => setFilterStatus("ALL")} />}
                        </Box>
                    )}
                </Paper>

                {/* ── Table ── */}
                {loading ? (
                    <TableShimmer rows={7} cols={9} />
                ) : error ? (
                    <Box sx={{ textAlign: "center", py: 8 }}><Typography color="error">⚠ {error}</Typography></Box>
                ) : (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden", animation: `${fadeUp} 0.4s ease-out 120ms both` }}>
                        <TableContainer sx={{ maxHeight: "65vh" }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {["#", "Subject", "Class & Medium", "Monthly", "Quarterly", "Half-Yearly", "Yearly", "Status", ""].map((h, i) => (
                                            <TableCell key={i} align={i === 8 ? "right" : "left"}
                                                sx={{ fontWeight: 700, fontSize: "0.72rem", letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary", background: "#f8fafc", py: 1.5 }}
                                            >{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filtered.map((sub, idx) => {
                                        const cId = sub.classId || sub.schoolClass?.id;
                                        const mId = sub.mediumId || sub.medium?.id;
                                        return (
                                            <TableRow key={sub.id} hover sx={{ "&:hover": { background: "#f0fafa" }, transition: "background 0.15s" }}>
                                                <TableCell sx={{ color: "text.disabled", fontSize: "0.75rem", width: 40 }}>
                                                    {idx + 1}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700} color={PRIMARY}>{sub.name}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={0.75}>
                                                        <Chip label={getClassName(cId)} size="small" sx={{ background: "rgba(23,65,67,0.08)", color: PRIMARY, fontWeight: 600, fontSize: "0.7rem" }} />
                                                        <Chip label={getMediumName(mId)} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">₹{sub.monthlyPrice ?? 0}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">₹{sub.quarterlyPrice ?? 0}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">₹{sub.halfYearlyPrice ?? 0}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700}>₹{sub.yearlyPrice ?? 0}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {sub.purchasable
                                                        ? <Chip label="Active" size="small" sx={{ background: "#dcfce7", color: "#166534", fontWeight: 700, fontSize: "0.7rem" }} />
                                                        : <Chip label="Inactive" size="small" sx={{ background: "#f1f5f9", color: "#64748b", fontWeight: 700, fontSize: "0.7rem" }} />}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => handleOpenEdit(sub)}
                                                                sx={{ color: PRIMARY, "&:hover": { background: "rgba(23,65,67,0.08)" } }}>
                                                                <EditIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => handleDelete(sub.id)}
                                                                sx={{ color: "#ef4444", "&:hover": { background: "rgba(239,68,68,0.08)" } }}>
                                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {filtered.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 8, color: "text.disabled" }}>
                                                <SubjectIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3, display: "block", mx: "auto" }} />
                                                No subjects match your filters.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
            </Box>

            {/* ── Create / Edit Dialog ── */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, px: 3, py: 2.5, color: "#fff" }}>
                    <Typography variant="h6" fontWeight={800}>{editingId ? "Edit Subject" : "New Subject"}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.75 }}>{editingId ? "Update the subject details below." : "Fill in the details to create a new subject."}</Typography>
                </Box>

                <DialogContent dividers sx={{ pt: 2 }}>
                    <Stack spacing={2.5}>
                        <TextField
                            label="Subject Name *"
                            fullWidth size="small"
                            value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                            autoFocus
                        />

                        <Stack direction="row" spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Class *</InputLabel>
                                <Select value={form.classId} label="Class *" onChange={e => setForm({ ...form, classId: Number(e.target.value) })}>
                                    {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Medium *</InputLabel>
                                <Select value={form.mediumId} label="Medium *" onChange={e => setForm({ ...form, mediumId: Number(e.target.value) })}>
                                    {mediums.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Stack>

                        <Divider>
                            <Chip label="Pricing" size="small" icon={<RupeeIcon />} sx={{ fontWeight: 700, color: PRIMARY }} />
                        </Divider>

                        <Stack direction="row" spacing={2}>
                            {([["Monthly", "monthlyPrice"], ["Quarterly", "quarterlyPrice"]] as [string, keyof SubjectUpsertDto][]).map(([lbl, key]) => (
                                <TextField key={key} label={lbl} type="number" fullWidth size="small"
                                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                    value={(form as any)[key]}
                                    onChange={e => setForm({ ...form, [key]: Number(e.target.value) })}
                                />
                            ))}
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            {([["Half-Yearly", "halfYearlyPrice"], ["Yearly", "yearlyPrice"]] as [string, keyof SubjectUpsertDto][]).map(([lbl, key]) => (
                                <TextField key={key} label={lbl} type="number" fullWidth size="small"
                                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                    value={(form as any)[key]}
                                    onChange={e => setForm({ ...form, [key]: Number(e.target.value) })}
                                />
                            ))}
                        </Stack>

                        <FormControlLabel
                            control={<Switch checked={form.purchasable} onChange={e => setForm({ ...form, purchasable: e.target.checked })}
                                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: ACCENT }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { background: ACCENT } }} />}
                            label={<Typography variant="body2" fontWeight={600}>Purchasable (show in student app)</Typography>}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenDialog(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={saving}
                        sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, px: 3, borderRadius: 2 }}>
                        {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : (editingId ? "Update Subject" : "Create Subject")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert onClose={() => setSnackbar(null)} severity={snackbar?.type ?? "info"} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar?.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}